import { ArrowLeft, LockKeyhole, Mail, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';

import { CONTACT_INFO } from '@/shared/components/home/constants';
import { IronviewLogo } from '@/shared/components/IronviewLogo';

const sections = [
  {
    title: 'Information we collect',
    content: (
      <>
        <p>Ironview collects only the information needed to provide and secure the service:</p>
        <ul>
          <li>
            <strong>Account and contact information</strong>, such as your name, email address,
            phone number, company, job title, role, and account identifiers.
          </li>
          <li>
            <strong>Project and user content</strong>, such as project details, drawings,
            documents, comments, RFIs, observations, punch items, daily logs, and other
            information you or your organization enter into Ironview.
          </li>
          <li>
            <strong>Photos, videos, scans, and files</strong> that you choose to capture, select,
            upload, download, or share through the service.
          </li>
          <li>
            <strong>Support communications</strong>, including messages, contact details, and
            optional attachments submitted when requesting help.
          </li>
          <li>
            <strong>Limited technical and diagnostic information</strong>, such as IP address,
            browser or device type, app version, timestamps, authentication events, error
            reports, and service-performance logs.
          </li>
        </ul>
      </>
    ),
  },
  {
    title: 'Device permissions',
    content: (
      <>
        <p>
          Ironview requests access only when a feature needs it. Camera and photo-library access
          lets you capture, select, upload, or save project media. You control these permissions
          in your device settings.
        </p>
        <p>
          If you enable Face ID or another device authentication method, the biometric
          information is processed by your device. Ironview does not receive or store your
          biometric data. The mobile app does not collect your device’s precise or approximate
          location.
        </p>
      </>
    ),
  },
  {
    title: 'How we use information',
    content: (
      <ul>
        <li>Provide, maintain, and improve Ironview’s web and mobile services.</li>
        <li>Authenticate users and enforce organization, project, and role permissions.</li>
        <li>Store, process, and display project records and media requested by users.</li>
        <li>Respond to support requests and communicate about service operations.</li>
        <li>Protect accounts, investigate errors, and prevent misuse or security incidents.</li>
        <li>Meet contractual and legal obligations.</li>
      </ul>
    ),
  },
  {
    title: 'How we share information',
    content: (
      <>
        <p>
          Project information is shared with authorized members of your organization and project
          team according to configured permissions. We may also use service providers for cloud
          hosting and storage, email delivery, mapping, software updates, and error or performance
          monitoring. Those providers process information only to deliver services to Ironview.
        </p>
        <p>
          We may disclose information when required by law, to protect rights and safety, or as
          part of a business transaction subject to appropriate safeguards. Ironview does not sell
          personal information and does not use personal information for cross-app tracking or
          targeted third-party advertising.
        </p>
      </>
    ),
  },
  {
    title: 'Data retention and security',
    content: (
      <>
        <p>
          We retain information while an account or customer relationship is active and as needed
          to provide project records, satisfy contractual or legal requirements, resolve disputes,
          and protect the service. Retention may also be governed by your organization’s agreement
          with Ironview.
        </p>
        <p>
          We use administrative, technical, and organizational safeguards designed to protect
          information. No method of storage or transmission is completely secure, so we cannot
          guarantee absolute security.
        </p>
      </>
    ),
  },
  {
    id: 'your-choices',
    title: 'Your choices',
    content: (
      <>
        <p>
          You may update certain account information in Ironview or through your organization’s
          administrator. You can also control camera, photo-library, and biometric permissions in
          your device settings.
        </p>
        <p>
          You can permanently delete your personal account in the Ironview iOS app by opening{' '}
          <strong>My Account</strong>, selecting <strong>Profile</strong>, and choosing{' '}
          <strong>Delete Account</strong>. The deletion takes effect immediately. Organization-owned
          project records may be retained when required for contractual, legal, security, or audit
          purposes, without retaining your active personal account.
        </p>
        <p>
          To request access, correction, or another privacy action, email{' '}
          <a href={'mailto:' + CONTACT_INFO.email}>{CONTACT_INFO.email}</a>.
        </p>
      </>
    ),
  },
  {
    title: 'Children’s privacy',
    content: (
      <p>
        Ironview is a business service intended for authorized construction professionals and is
        not directed to children under 13. We do not knowingly collect personal information from
        children under 13.
      </p>
    ),
  },
  {
    title: 'Changes to this policy',
    content: (
      <p>
        We may update this policy as our services or legal obligations change. We will post the
        revised policy here and update the effective date. Material changes may also be
        communicated through the service or by email.
      </p>
    ),
  },
];

const PrivacyPolicyPage = () => {
  useEffect(() => {
    document.title = 'Privacy Policy | Ironview';
  }, []);

  return (
    <div className="min-h-screen bg-maintenance-dark text-white">
      <header className="border-b border-white/10 bg-maintenance-navy/95">
        <div className="mx-auto flex max-w-6xl items-center justify-between gap-6 px-5 py-4 sm:px-8">
          <a href="/" aria-label="Ironview home" className="inline-flex items-center">
            <IronviewLogo tone="light" className="w-48 sm:w-56" />
          </a>
          <a
            href="/"
            className="inline-flex min-h-11 items-center gap-2 rounded-[10px] border border-white/20 px-4 text-sm font-semibold text-white/80 transition hover:border-white/50 hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Ironview
          </a>
        </div>
      </header>

      <main>
        <section className="relative overflow-hidden border-b border-white/10">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(242,104,12,0.18),transparent_42%)]" />
          <div className="relative mx-auto max-w-6xl px-5 py-14 sm:px-8 sm:py-20">
            <div className="max-w-3xl">
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-primary">
                <ShieldCheck className="h-4 w-4" />
                Privacy at Ironview
              </div>
              <h1 className="text-4xl font-black tracking-tight sm:text-5xl md:text-6xl">
                Privacy Policy
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-7 text-white/65 sm:text-lg">
                This policy explains how Ironview 360 LLC collects, uses, and protects
                information through the Ironview website, web application, and mobile app.
              </p>
              <p className="mt-5 text-sm font-semibold text-white/45">
                Effective September 29, 2026
              </p>
            </div>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-8 px-5 py-10 sm:px-8 sm:py-14 lg:grid-cols-[0.34fr_1fr]">
          <aside className="lg:sticky lg:top-6 lg:self-start">
            <div className="rounded-[1.375rem] border border-white/10 bg-maintenance-slate/70 p-6">
              <LockKeyhole className="h-6 w-6 text-primary" />
              <h2 className="mt-4 text-lg font-bold">Plain-language privacy</h2>
              <p className="mt-2 text-sm leading-6 text-white/55">
                We collect the information needed to run Ironview, keep projects secure, and
                support users. We do not sell personal information or use it for cross-app
                advertising.
              </p>
              <a
                href={'mailto:' + CONTACT_INFO.email}
                className="mt-5 inline-flex min-h-11 items-center gap-2 font-semibold text-primary hover:brightness-110"
              >
                <Mail className="h-4 w-4" />
                Privacy questions
              </a>
            </div>
          </aside>

          <article className="rounded-[1.375rem] bg-white px-6 py-3 text-slate-700 shadow-2xl shadow-black/20 sm:px-10">
            {sections.map((section) => (
              <section
                key={section.title}
                id={section.id}
                className="border-b border-slate-200 py-7 last:border-0"
              >
                <h2 className="text-xl font-black tracking-tight text-slate-950">
                  {section.title}
                </h2>
                <div className="mt-3 space-y-3 text-sm leading-7 sm:text-base [&_a]:font-semibold [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-4 [&_li]:ml-5 [&_li]:list-disc [&_li]:pl-1">
                  {section.content}
                </div>
              </section>
            ))}

            <section className="border-t border-slate-200 py-7">
              <h2 className="text-xl font-black tracking-tight text-slate-950">Contact us</h2>
              <address className="mt-3 not-italic leading-7">
                Ironview 360 LLC
                <br />
                Email: <a href={'mailto:' + CONTACT_INFO.email}>{CONTACT_INFO.email}</a>
                <br />
                Phone:{' '}
                <a href={'tel:' + CONTACT_INFO.phone.replace(/\./g, '')}>
                  {CONTACT_INFO.phone}
                </a>
              </address>
            </section>
          </article>
        </section>
      </main>

      <footer className="border-t border-white/10 px-5 py-6 text-center text-sm text-white/40">
        <a href="/support" className="font-semibold text-white/60 hover:text-white">
          Support
        </a>
        <span aria-hidden="true"> · </span>
        {CONTACT_INFO.copyright}
      </footer>
    </div>
  );
};

export default PrivacyPolicyPage;
